import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Zap, ArrowLeft, MailCheck } from "lucide-react";
import { formatApiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex items-center justify-center p-6">
      <Toaster theme="dark" richColors position="top-right" />
      <Card className="w-full max-w-md p-8 rounded-sm bg-card border-border">
        <Link to="/login" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-1 mb-4">
          <ArrowLeft className="h-3 w-3" />Back to sign in
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center"><Zap className="h-4 w-4 text-white" /></div>
          <div className="font-display font-extrabold">LeadifyCRM</div>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <MailCheck className="h-12 w-12 text-primary mx-auto mb-3" />
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Check your inbox.</h2>
            <p className="text-sm text-muted-foreground mt-2">If an account exists for <span className="text-white">{email}</span>, we've sent a reset link that expires in 1 hour.</p>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-2">Password reset</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight mb-6">Forgot your password?</h2>
            <p className="text-sm text-muted-foreground mb-6">Enter the email tied to your account and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-sm bg-secondary/40" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary hover:bg-primary/90 h-11" data-testid="forgot-submit-button">
                {loading ? "…" : "Send reset link"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
